import { createMiddleware, createStart } from '@tanstack/react-start';
import { APP_VERSION_HEADER, APP_VERSION_STORAGE_KEY } from '@/lib/app-version';

function syncVersionFromHeader(response: Response) {
	if (typeof window === 'undefined') {
		return;
	}

	const version = response.headers.get(APP_VERSION_HEADER);
	if (!version) {
		return;
	}

	const currentVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
	if (!currentVersion) {
		window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
		return;
	}

	if (currentVersion !== version) {
		window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
	}
}

const appVersionAwareFetch = Object.assign((...args: Parameters<typeof fetch>) => {
	return fetch(...args).then((response) => {
		syncVersionFromHeader(response);
		return response;
	});
}, fetch);

const appVersionHeaderMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const result = await options.next();

	if (result?.response?.headers) {
		const deployedVersion = process.env.VITE_APP_VERSION ?? 'dev-local';
		result.response.headers.set(APP_VERSION_HEADER, deployedVersion);
	}
	return result;
});

// Canonical request wide event: one event per HTTP request, enriched mid-request by
// handlers (see `@/lib/wide-event.server`) and emitted once at the end. The shared core
// buffers + batch-sends, so this never blocks the response.
//
// `wide-event.server` is server-only (pulls in sst + the monolith transport). It's loaded
// via dynamic import *inside* `.server()` so it never enters the client bundle (this start
// instance is isomorphic — `serverFns.fetch` runs in the browser).
const requestWideEventMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const wide = await import('@/lib/wide-event.server');
	// `options.next()` may return a value or a promise; normalize to a promise.
	return wide.instrumentPortalsRequest(() => Promise.resolve(options.next()));
});

export const startInstance = createStart(() => ({
	requestMiddleware: [requestWideEventMiddleware, appVersionHeaderMiddleware],
	serverFns: {
		fetch: appVersionAwareFetch,
	},
}));
